import { useAuth } from '@/hooks/useAuth'
import { cn } from '@/lib/cn'

interface HeaderProps {
  presenting: boolean
  onTogglePresenting: () => void
  isDark: boolean
  onToggleTheme: () => void
  sidebarCollapsed: boolean
  onToggleSidebar: () => void
}

export function Header({
  presenting,
  onTogglePresenting,
  isDark,
  onToggleTheme,
  sidebarCollapsed,
  onToggleSidebar,
}: HeaderProps) {
  const { user } = useAuth()

  return (
    <header
      className={cn(
        'fixed top-0 right-0 z-20 h-14 flex items-center justify-between px-6 bg-[var(--surface-elevated)] border-b border-[var(--border)] transition-all duration-200',
        sidebarCollapsed ? 'left-16' : 'left-56',
      )}
    >
      <div className="flex items-center gap-3">
        {/* Sidebar toggle */}
        <button
          onClick={onToggleSidebar}
          className="p-1.5 rounded-lg text-[var(--content-tertiary)] hover:text-[var(--content)] hover:bg-[var(--surface-nested)] transition-colors cursor-pointer"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
          </svg>
        </button>
      </div>

      <div className="flex items-center gap-3">
        {/* Presentation mode toggle */}
        <button
          onClick={onTogglePresenting}
          className={cn(
            'flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer',
            presenting
              ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
              : 'bg-[var(--surface-nested)] text-[var(--content-tertiary)] hover:text-[var(--content-secondary)]',
          )}
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            {presenting ? (
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
            )}
          </svg>
          {presenting ? 'PII Hidden' : 'PII Visible'}
        </button>

        {/* Dark mode toggle */}
        <button
          onClick={onToggleTheme}
          className="p-1.5 rounded-lg text-[var(--content-tertiary)] hover:text-[var(--content)] hover:bg-[var(--surface-nested)] transition-colors cursor-pointer"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            {isDark ? (
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v2.25m6.364.386l-1.591 1.591M21 12h-2.25m-.386 6.364l-1.591-1.591M12 18.75V21m-4.773-4.227l-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" d="M21.752 15.002A9.718 9.718 0 0118 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 003 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 009.002-5.998z" />
            )}
          </svg>
        </button>

        {/* Admin user */}
        {user && (
          <div className="flex items-center gap-2 pl-3 border-l border-[var(--border)]">
            <div className="w-7 h-7 rounded-full bg-[var(--brand)] flex items-center justify-center text-white text-xs font-semibold">
              {(user.name ?? user.email).charAt(0).toUpperCase()}
            </div>
          </div>
        )}
      </div>
    </header>
  )
}
